import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { useUserStore } from '../../store/user.store'
import { observer } from "mobx-react"
import { useEffect, useState, useCallback } from "react"
import { Box, Button, CircularProgress, Paper, Typography, useTheme } from "@mui/material"
import { LoginOutlined } from "@mui/icons-material"

// Assets & Stores
import warningImg from '../../assets/waiting.svg'
import alivelogo from '../../assets/alivelogo.png'
import noConnectionImg from '../../assets/no-connection.svg'
import serverDownImg from '../../assets/server-down.svg'
import { useSettingsStore } from "../../store/settings.store"
import { useLayoutContext } from "../../contexts/layout.context"
import { useAuthStore } from "../../store/auth.store"
import { ERPLoginPageURL } from "../../utils/utilities"
import { ErrorBoundary } from 'react-error-boundary'
import FallbackErrorBoundary from '../error-boundaries/FallbackErrorBoundary'

const RequireAuth = observer( ( { allowedRoles } ) => {
    const [errorCode, setErrorCode] = useState( null )
    const [loading, setLoading] = useState( true )

    const { setActive } = useLayoutContext()
    const SettingsStore = useSettingsStore()
    const AuthStore = useAuthStore()
    const UserStore = useUserStore()

    const navigate = useNavigate()
    const { pathname, search } = useLocation()
    const { theme } = useTheme()

    // --- Helper Functions ---
    const navigateToSignin = () => {
        if ( pathname?.includes( 'assessment' ) && !pathname?.includes( 'admin' ) ) {
            return navigate( `/assessment/login`, { replace: true } )
        }
        const redirectValue = encodeURIComponent( `${pathname}${search}` )
        if ( pathname.includes( 'admin' ) ) {
            navigate( `/admin?redirect=${redirectValue}`, { replace: true } )
        } else {
            window.location.href = `${ERPLoginPageURL}?redirect=${redirectValue}`
        }
    }

    const handleConnectionStatusChange = useCallback( () => {
        // if ( !window.navigator.onLine ) setErrorCode( "noInternet" )
    }, [] )

    // --- Lifecycle Effects ---
    useEffect( () => {
        setActive( pathname )
    }, [pathname, setActive] )

    useEffect( () => {
        const initAuth = async () => {
            const { netError } = await UserStore.fetchUser()

            if ( netError ) {
                if ( !window.navigator.onLine ) setErrorCode( "noInternet" )
                else setErrorCode( "serverConnectionError" )
            } else {
                const preRequisiteRequests = []
                const role = UserStore.getUser.user_role?.toLowerCase()

                if ( !['admin', 'subadmin'].includes( role ) ) {
                    preRequisiteRequests.push( UserStore.fetchUserSubjects( true ) )
                    if ( role !== 'student' ) {
                        preRequisiteRequests.push( UserStore.fetchUserAcademicYears() )
                        preRequisiteRequests.push( UserStore.fetchUsersAllBatchesAndSectionList( true ) )
                    }
                }
                preRequisiteRequests.push( SettingsStore.fetchSettings( { filter: !['admin', 'subadmin'].includes( role ) ? 'foruse' : 'all' } ) )
                await Promise.allSettled( preRequisiteRequests )
            }
            setLoading( false )
        }

        initAuth()
        window.addEventListener( 'online', handleConnectionStatusChange )
        window.addEventListener( 'offline', handleConnectionStatusChange )
        return () => {
            window.removeEventListener( 'online', handleConnectionStatusChange )
            window.removeEventListener( 'offline', handleConnectionStatusChange )
        }
    }, [UserStore, SettingsStore, handleConnectionStatusChange] )

    // --- Shared Layout Wrapper ---
    const StatusPageWrapper = ( { children, overlayOpacity = 0.8 } ) => (
        <Box
            sx={{ background: `url('/imgs/bg-pattern1.png')`, position: 'relative' }}
            display="flex" height="100%" alignItems="center" justifyContent="center"
        >
            {theme === 'dark' && (
                <Box sx={{
                    zIndex: 5, position: "absolute",
                    inset: 0, background: `rgba(0,0,0,${overlayOpacity})`
                }} />
            )}
            <Paper sx={{
                zIndex: 10, minWidth: "220px", padding: "20px",
                display: "flex", flexDirection: "column", alignItems: "center", gap: "20px"
            }}>
                {children}
            </Paper>
        </Box>
    )

    // --- Render Logic (Early Returns) ---

    // 1. Loading State
    if ( loading ) {
        return (
            <StatusPageWrapper overlayOpacity={0.7}>
                <img width="70px" src={alivelogo} alt="Alive" />
                <Typography variant="subtitle2" display="flex" alignItems="center" gap="10px">
                    <CircularProgress size={14} /> Loading Alive...
                </Typography>
            </StatusPageWrapper>
        )
    }

    // 2. Connection/Server Error States
    if ( errorCode ) {
        return (
            <StatusPageWrapper>
                {errorCode === "noInternet" ? (
                    <>
                        <img width="70px" src={noConnectionImg} alt="No Internet" />
                        <Typography variant="subtitle2">Check your internet connection</Typography>
                    </>
                ) : (
                    <>
                        <img width="150px" src={serverDownImg} alt="Server Error" />
                        <Typography variant="subtitle2">Server cannot be reached! try again later</Typography>
                    </>
                )}
            </StatusPageWrapper>
        )
    }

    // 3. Authorization & Session Check
    const userRole = UserStore.getUser['user_role']?.toUpperCase()
    const isAuthorized = allowedRoles.includes( userRole )

    if ( !isAuthorized ) {
        const sessionEnded = AuthStore.getErpSessionState === "ended"
        return (
            <StatusPageWrapper>
                <img width="250px" src={warningImg} alt="Warning" />
                <Typography variant='subtitle2' textAlign="center">
                    {sessionEnded
                        ? "Your session has ended please log in again!"
                        : <>You are not allowed to access this page. This page is only available for <em>{allowedRoles.join( ", " )}.</em></>
                    }
                </Typography>
                <Button
                    variant="outlined" startIcon={<LoginOutlined />}
                    color="primary" onClick={navigateToSignin}
                    sx={{ textTransform: "capitalize" }}
                >
                    Sign in
                </Button>
            </StatusPageWrapper>
        )
    }

    // 4. Authorized Access
    return <ErrorBoundary FallbackComponent={FallbackErrorBoundary} >
        <Outlet />
    </ErrorBoundary>
} )

export default RequireAuth